import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client';
import {
  IconButton,
  IDropdownOption,
  Label,
  Separator,
  Stack,
  TooltipDelay,
  TooltipHost,
} from '@fluentui/react';
import {
  FormikCheckBox,
  FormikDropdown,
  FormikTextField,
} from 'common/components';
import { formatDropdownOptions } from 'common/utils';
import { getGlobalDateFormat } from 'common/utils/dateFormats';
import { useField, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import {
  UserSetupCommonData,
  UserSetupCommonData_companyDepartmentOccupationTemplates_nodes_departmentOccupationTemplate,
} from 'settings/account/userSetup/__generated__/UserSetupCommonData';
import { AttachableUserProfileDocuments_attachableUserProfileDocuments_nodes } from '../__generated__/AttachableUserProfileDocuments';
import {
  UserProfileEmail,
  UserProfileEmailVariables,
} from '../__generated__/UserProfileEmail';
import { UserSetupCommon } from '../__generated__/UserSetupCommon';
import { ApprovalTypeView } from '../ApprovalTypeView';
import { UserSetupValues } from '../types';
import { useStyles } from './index.styles';

export type AttachableDataType =
  AttachableUserProfileDocuments_attachableUserProfileDocuments_nodes;
export interface AttachedDocumentsDataType {
  documentTypeId: number | null | undefined;
  entityDocumentId: string[] | null;
  attachedDocuments: AttachableDataType[];
}

import GET_USERPROFILE_COMMON_TYPES from '../../UserSetupCommonData.graphql';
import GET_COMPANY_USER_OCCUPATION_TITLES from '../../CompanyUserOccupationTitles.graphql';

import COMMON_DATA from '../../view/UserSetupCommon.graphql';
import GET_USERPROFILE_EMAIL from '../UserProfileEmail.graphql';
import { OptionWithIcons } from 'common/components/RankOptionUpdateComponent/OptionWithIcons';
import { rankOptionType } from 'common/components/RankOptionUpdateComponent/types';
import { EntityDeleteInput } from 'common/types/globalTypes';
import { RankOptionViewModal } from 'common/components/RankOptionUpdateComponent/RankOptionViewModal';
import { RankOptionDelete } from 'common/components/RankOptionUpdateComponent/RankOptionDelete';
import { OnRenderRankLabel } from 'common/components/RankOptionUpdateComponent/OnRenderRankLabel';
import { setUserDefaults } from 'utility/cache/ui';
import { OccupationViewModal } from 'settings/account/occupations/view/OccupationViewModal';
import { UserProfileStatus } from 'common/constants';

interface BasicFormProps {
  toDeleteRows: (id: string, _rowTimestamp: string) => void;
  inputsDisabled: boolean;
   _isApproved: boolean | null | undefined

  isNew: boolean;
  approvalStateDisable: boolean;
  isUpdatable: boolean;
  userId: string | undefined;
  isTemplateControlled: boolean;
  setIsTemplateControlled: React.Dispatch<React.SetStateAction<boolean>>
  templateControlledDeptId: boolean
  setTemplateControlledDeptId: React.Dispatch<React.SetStateAction<boolean>>
}

interface UserTemplateOptions extends IDropdownOption {
  key: string | number;
  text: string;
  departmentOccupationTemplate: UserSetupCommonData_companyDepartmentOccupationTemplates_nodes_departmentOccupationTemplate | null;
}

export const BasicForm: React.FC<BasicFormProps> = ({
  toDeleteRows,
  inputsDisabled,
  _isApproved,
  isNew,
  approvalStateDisable,
  isUpdatable,
  userId,
  isTemplateControlled,
  setIsTemplateControlled,
  templateControlledDeptId,
  setTemplateControlledDeptId
}) => {
  const styles = useStyles();
  const userDefaults = useReactiveVar(setUserDefaults);
  const { data: commonData } = useQuery<UserSetupCommonData>(
    GET_USERPROFILE_COMMON_TYPES
  );
  const { data: commonTypes } = useQuery<UserSetupCommon>(COMMON_DATA);
  const [openRankModal, setOpenRankModal] = useState<boolean>(false);

  const [rankDeleteDialog, setRankDeleteDialog] = useState(false);
  const [rankDeleteEntity, setRankDeleteEntity] = useState<
    EntityDeleteInput | undefined
  >(undefined);
  const [selectedOption, setSelectedOption] = useState<
    rankOptionType | undefined
  >(undefined);
  const [showOccupationModal, setShowOccupationModal] = useState(false);
  const [newOccupation, setNewOccupation] = useState<string | undefined>(
    undefined
  );
  const {
    refetch: refetchOccupationTitles,
  } = useQuery(GET_COMPANY_USER_OCCUPATION_TITLES);

  const occTemplateOptions: UserTemplateOptions[] =
    commonData?.companyDepartmentOccupationTemplates?.nodes.map((item) => ({
      key: item.departmentOccupationTemplateId,
      text: item.departmentOccupationTemplate?.name || '',
      departmentOccupationTemplate: item.departmentOccupationTemplate,
    })) || [];

  const { values, setFieldValue, setFieldTouched, validateForm } = useFormikContext<UserSetupValues>();

  useEffect(() => {
    validateForm();
  }, [isTemplateControlled, templateControlledDeptId, validateForm,]);

  const [field] = useField('userRankTypeId');

  const roleOptions = formatDropdownOptions(commonData?.roles?.nodes, {
    getKey: (item) => item.id,
    getText: (item) => item.name!,
  });
  const occTitleOptions = formatDropdownOptions(
    commonData?.companyUserOccupationTitles?.nodes,
    {
      getKey: (item) => item.userOccupationTitleId,
      getText: (item) => item.userOccupationTitles?.userOccupationTitle!,
    }
  );
  const departmentOptions = formatDropdownOptions(
    commonData?.companyDepartments?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
    }
  );
  const departmentGroupOptions = formatDropdownOptions(
    commonData?.companyDepartmentGroups?.nodes,
    {
      getKey: (item) => item.departmentGroupId,
      getText: (item) => item.departmentGroup?.name!,
    }
  );
  const accessPolicyOptions = formatDropdownOptions(
    commonData?.companyDataAccessPolicies?.nodes,
    {
      getKey: (item) => item.dataAccessPolicyId,
      getText: (item) => item.dataAccessPolicy?.name!,
    }
  );
  const userGroupOptions = formatDropdownOptions(
    commonData?.companyUserGroups?.nodes,
    {
      getKey: (item) => item.userGroupId,
      getText: (item) => item.userGroup?.name!,
    }
  );
  const accessGroupOptions = formatDropdownOptions(
    commonData?.companyAccessGroups?.nodes,
    {
      getKey: (item) => item.accessGroupId,
      getText: (item) => item.accessGroup?.name!,
    }
  );
  const tagGroupOptions = formatDropdownOptions(
    commonData?.companyTagGroups?.nodes,
    {
      getKey: (item) => item.tagGroupId,
      getText: (item) => item.tagGroup?.name!,
    }
  );
  const communicationGroupOptions = formatDropdownOptions(
    commonData?.companyCommunicationGroups?.nodes,
    {
      getKey: (item) => item.communicationGroupId,
      getText: (item) => item.communicationGroup?.name!,
    }
  );

  const rankOptions: IDropdownOption[] =
    commonTypes?.userRankTypes?.nodes.map((item) => ({
      key: item.id,
      text: item.userRank,
      rowTimestamp: item._rowTimestamp,
      isUpdatable: item._isUpdatable,
      isDeletable: item._isDeletable,
    })) || [];
  const securityLevelOptions = formatDropdownOptions(
    commonTypes?.secureRowLevels?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
    }
  );
  interface invitationOptionType extends IDropdownOption {
    key: string | number;
    text: string;
    isDefault: boolean;
    disabled: boolean;
  }
  const invitationTypeOptions: invitationOptionType[] =
    commonData?.userInviteStatusTypes?.nodes.map((item) => ({
      key: item.id,
      text: item.statusType || '',
      isDefault: item.isDefault ?? false,
      disabled: !item.isSelectable,
    })) || [];

  const defaultItemId =
    commonData?.userInviteStatusTypes?.nodes.find((item) => item.isDefault)
      ?.id || null;


  const onTransactionTypeChange = async (
    selectedOption: UserTemplateOptions
  ) => {
    const template = selectedOption.departmentOccupationTemplate;
    if (!template) return;

    setFieldValue('departmentOccupationTemplateId', selectedOption.key);
    setTemplateControlledDeptId(!!template.isTemplateControlled && !!template.departmentId);
    setIsTemplateControlled(!!template.isTemplateControlled)

    const fields = [
      'roleId',
      'departmentId',
      'departmentGroupId',
      'dataAccessPolicyId',
      'userOccupationTitleId',
      'userGroupId',
      'accessGroupId',
      'tagGroupId',
      'communicationGroupId',
    ] as const;

    fields.forEach((field) => {
      if (template[field] !== undefined) {
        setFieldValue(field, template[field]);
      }
    });
  };

  const inviteStatusDisabled = inputsDisabled || approvalStateDisable || !!isTemplateControlled;

  const [getUserEmail, { data: userProfileEmail }] = useLazyQuery<
    UserProfileEmail,
    UserProfileEmailVariables
  >(GET_USERPROFILE_EMAIL, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const refreshData = async () => {
      if (newOccupation) {
        await refetchOccupationTitles();
        setFieldValue('userOccupationTitleId', newOccupation);
        setNewOccupation(undefined);
        setShowOccupationModal(false);
      }
    };
    refreshData();
  }, [newOccupation, setFieldValue, refetchOccupationTitles]);

  return (
    <Stack grow tokens={{ childrenGap: 20, padding: '20px 35px 30px' }}>
      <Stack horizontal grow tokens={{ childrenGap: 20 }}>
        <Stack.Item className={styles.flex50}>
          <FormikTextField
            name="name"
            label="Name"
            placeholder="Enter name"
            disabled={inputsDisabled || approvalStateDisable}
            required
          />
        </Stack.Item>
        <Stack.Item className={styles.flex50}>
          <FormikTextField
            name="description"
            label="Description"
            placeholder="Enter description"
            disabled={inputsDisabled || isNew}
          />
        </Stack.Item>
      </Stack>
      <Stack horizontal grow tokens={{ childrenGap: 20 }}>
        <Stack.Item
          className={styles.flex50}
          onMouseEnter={() => {
            if (userId)
              getUserEmail({
                variables: {
                  userProfileId: userId,
                },
              });
          }}
        >
          <TooltipHost
            content={userProfileEmail?.userProfileEmail ?? ''}
            delay={TooltipDelay.long}
          >
            <FormikTextField
              name="emailAccount"
              label="Email"
              placeholder="Enter email"
              disabled={inputsDisabled || approvalStateDisable}
              required
            />
          </TooltipHost>
        </Stack.Item>
        <Stack.Item className={styles.flex50}>
          <FormikDropdown
            placeholder="Select"
            label="User Template"
            name="departmentOccupationTemplateId"
            options={occTemplateOptions}
            disabled={inputsDisabled || !!_isApproved}
            onChange={(_, option) => {
              onTransactionTypeChange(option as UserTemplateOptions);
            }}
          />
        </Stack.Item>
      </Stack>
      <Stack horizontal grow tokens={{ childrenGap: 20 }}>
        <Stack.Item className={styles.flex50}>
          <FormikDropdown
            label="Role"
            placeholder="Select"
            options={roleOptions}
            name="roleId"
            disabled={inputsDisabled || !!isTemplateControlled}
            required
          />
        </Stack.Item>
        <Stack
          tokens={{
            childrenGap: 20,
          }}
          className={styles.flex50}
        >
          <Stack.Item
            style={{ flex: 1 }}
          >
            <FormikDropdown
              label="Occupation"
              placeholder="Select"
              options={occTitleOptions}
              name="userOccupationTitleId"
              disabled={inputsDisabled}
              onChange={(_, value) => {
                if (value) {
                  setFieldValue('description', value.text);
                  setFieldValue('userOccupationTitleId', value.key, true); // true = validate
                  setFieldTouched('userOccupationTitleId', true, false);
                }
              }}
              required
              onRenderLabel={(props, defaultRender) => (
                <Stack horizontal verticalAlign="center">
                  {defaultRender?.(props)}
                  <IconButton
                    iconProps={{ iconName: 'AddTo' }}
                    onClick={() => setShowOccupationModal(true)}
                    styles={{ root: { height: 20, width: 10 } }}
                  />
                </Stack>
              )}
            />
            {showOccupationModal && (
              <OccupationViewModal
                setNewOption={setNewOccupation}
                onClose={() => {
                  if (!newOccupation) setShowOccupationModal(false);
                }}
              />
            )}

          </Stack.Item>

        </Stack>
      </Stack>
      <Stack horizontal grow tokens={{ childrenGap: 20 }}>
        <Stack.Item className={styles.flex50}>
          <FormikDropdown
            label="Department"
            placeholder="Select"
            options={departmentOptions}
            name="departmentId"
            disabled={inputsDisabled || templateControlledDeptId}
            required
          />
        </Stack.Item>
        <Stack.Item className={styles.flex50}>
          <FormikDropdown
            label="Department Group"
            placeholder="Select"
            name="departmentGroupId"
            options={departmentGroupOptions}
            disabled={inputsDisabled || templateControlledDeptId}
          />
        </Stack.Item>
      </Stack>
      <Stack horizontal grow tokens={{ childrenGap: 20 }}>
        <Stack.Item className={styles.flex50}>
          <FormikDropdown
            label="Access Policy"
            placeholder="Select"
            options={accessPolicyOptions}
            name="dataAccessPolicyId"
            disabled={inputsDisabled || !!isTemplateControlled}
            required
          />
        </Stack.Item>
        <Stack.Item className={styles.flex50}>
          <FormikDropdown
            label="User Group"
            placeholder="Select"
            name="userGroupId"
            options={userGroupOptions}
            disabled={inputsDisabled || templateControlledDeptId}
          />
        </Stack.Item>
      </Stack>
      <Stack horizontal grow tokens={{ childrenGap: 20 }}>
        <Stack.Item className={styles.flex50}>
          <FormikDropdown
            label="User Access Pool"
            placeholder="Select"
            name="accessGroupId"
            options={accessGroupOptions}
            disabled={inputsDisabled || templateControlledDeptId}
          />
        </Stack.Item>
        <Stack.Item className={styles.flex50}>
          <FormikDropdown
            label="Tagging Group"
            placeholder="Select"
            name="tagGroupId"
            options={tagGroupOptions}
            disabled={inputsDisabled || !!isTemplateControlled}
          />
        </Stack.Item>
      </Stack>
      <Stack horizontal grow verticalAlign="end" tokens={{ childrenGap: 20 }}>
        <Stack.Item className={styles.flex50}>
          <FormikDropdown
            label="Communication Group"
            placeholder="Select"
            name="communicationGroupId"
            options={communicationGroupOptions}
            disabled={inputsDisabled || !!isTemplateControlled}
          />
        </Stack.Item>
        <Stack.Item className={styles.flex50}>
          <FormikDropdown
            onRenderLabel={() => (
              <OnRenderRankLabel
                onIconClick={() => {
                  setOpenRankModal(true);
                }}
                disabled={inputsDisabled || !!isTemplateControlled}
              />
            )}
            placeholder="Select"
            name="userRankTypeId"
            options={rankOptions}
            disabled={inputsDisabled || !!isTemplateControlled}
            onRenderOption={(option) =>
              option ? (
                <OptionWithIcons
                  option={option as rankOptionType}
                  setRankDeleteEntity={setRankDeleteEntity}
                  setRankDeleteDialog={setRankDeleteDialog}
                  setOpenRankModal={setOpenRankModal}
                  setSelectedOption={setSelectedOption}
                  currentuserRankId={field.value}
                />
              ) : null
            }
          />
        </Stack.Item>
        {openRankModal && (
          <RankOptionViewModal
            onClose={() => {
              setOpenRankModal(false);
              setSelectedOption(undefined);
            }}
            onCreate={(createdId: string) => {
              setFieldValue('userRankTypeId', createdId);
            }}
            option={selectedOption}
          />
        )}

        {rankDeleteEntity && rankDeleteDialog && (
          <RankOptionDelete
            rankDeleteEntity={rankDeleteEntity}
            setRankDeleteDialog={setRankDeleteDialog}
            onClose={() => {
              setRankDeleteEntity(undefined);
              setOpenRankModal(false);
            }}
          />
        )}
      </Stack>
      <Stack horizontal grow verticalAlign="end" tokens={{ childrenGap: 20 }}>
        <Stack.Item className={styles.flex50}>
          <FormikDropdown
            label="Security Level"
            placeholder="Select"
            name="secureRowLevelId"
            options={securityLevelOptions}
            disabled={inputsDisabled || !!isTemplateControlled}
          />
        </Stack.Item>
        <Stack.Item className={styles.flex50}>
          <FormikDropdown
            label="Invite Status"
            placeholder="Select"
            name="_invitationStatusTypeId"
            options={invitationTypeOptions}
            disabled={inviteStatusDisabled}
            selectedKey={
              isNew && !values._invitationStatusTypeId
                ? defaultItemId
                : values._invitationStatusTypeId
            }
          />
        </Stack.Item>
      </Stack>
      <Stack horizontal grow verticalAlign="end" tokens={{ childrenGap: 20 }}>
        <Stack.Item className={styles.flex50}>
          <Stack
            horizontal
            className={styles.formikCheckbox}
            tokens={{ childrenGap: 20 }}
          >
            <FormikCheckBox
              disabled
              boxSide="end"
              name={`_signinInfo.isUserAdministrator`}
              label="User Adminsitrator"
            />
            <FormikCheckBox
              disabled
              boxSide="end"
              name={`_signinInfo.isProjectSupervisor`}
              label="Project Supervisor"
            />
          </Stack>
        </Stack.Item>
        <Stack.Item className={styles.flex50}>
          <Stack
            horizontal
            style={{ justifyContent: 'flex-start' }}
            tokens={{ childrenGap: 50 }}
          >
            <Stack>
              <Label>Last Sign-in</Label>
              <Label style={{ alignSelf: 'center' }}>
                {values._lastUsedDate
                  ? getGlobalDateFormat(values._lastUsedDate)
                  : 'Not available'}
              </Label>
            </Stack>
            <Stack>
              <Label>Active Approvals</Label>
              <Label style={{ alignSelf: 'center' }}>
                {values._activeApprovalCount ?? 'Not Available'}
              </Label>
            </Stack>
          </Stack>
        </Stack.Item>

      </Stack>
      <Stack style={{ marginTop: 30 }}>
        {!!userDefaults?._isNewProjectSelectionAvailable && <FormikCheckBox
          boxSide="end"
          name={`isNewProjectIncluded`}
          label="Include in New Projects?"
          disabled={inputsDisabled}
        />}
      </Stack>
      <Separator />
      <ApprovalTypeView
        approvalTypes={commonData?.approvalTypes?.nodes || []}
        deleteRow={toDeleteRows}
        inputsDisabled={!isUpdatable || !!isTemplateControlled}
      />
    </Stack>
  );
};
